import { ShieldAlert, ShieldCheck } from "lucide-react";
import { useEffect, useId, useState } from "react";

import { BlockedPeople } from "~/components/BlockedPeople";
import { ConfirmDialog } from "~/components/ConfirmDialog";
import { DeviceList, SectionLabel, type OwnDevicesState } from "~/components/DeviceList";
import { Dialog } from "~/components/Dialog";
import { DialogHeader } from "~/components/CreateRoomDialog";
import { buildKeyBackupRestoreMode, KeyBackupSetup } from "~/components/KeyBackupSetup";
import { useOptionalVerificationLauncher } from "~/components/VerificationLauncherContext";
import { VerificationPrimaryButton } from "~/components/verification-primitives";
import { useMatrix } from "~/context/MatrixContext";
import { getDeviceId, getOwnDevices, type MatrixDevice } from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

export function SettingsDialog({
  onClose,
  title = "Settings",
}: {
  onClose: () => void;
  title?: string;
}) {
  const titleId = `${useId()}-settings-title`;
  const {
    client,
    userId,
    signOut,
    markRecovered,
    retryCryptoBootstrap,
    recoveryView,
    resumeRecovery,
  } = useMatrix();
  const launcher = useOptionalVerificationLauncher();
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [devices, setDevices] = useState<OwnDevicesState>({ status: "loading" });
  const currentDeviceId = client ? getDeviceId(client) : null;
  const needsRecovery = recoveryView != null;

  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    setDevices({ status: "loading" });
    getOwnDevices(client)
      .then((next) => {
        if (!cancelled) setDevices({ status: "ready", devices: next });
      })
      .catch(() => {
        if (!cancelled) setDevices({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [client, needsRecovery]);

  const finishRecovery = () => {
    markRecovered();
    setRestoreOpen(false);
    void retryCryptoBootstrap();
  };

  if (restoreOpen && client) {
    return (
      <KeyBackupSetup
        mode={buildKeyBackupRestoreMode(client, finishRecovery, () => setRestoreOpen(false))}
      />
    );
  }

  const all = devices.status === "ready" ? devices.devices : [];
  const current = all.find((device) => device.deviceId === currentDeviceId) ?? null;
  const verified = !needsRecovery && current?.isVerified === true;

  const verifyThisDevice = () => {
    if (needsRecovery) {
      resumeRecovery();
      onClose();
      return;
    }
    const partner = bestVerifiedPartner(all, currentDeviceId);
    if (partner && launcher && userId) {
      void launcher.startVerification(userId, partner.deviceId);
      return;
    }
    setRestoreOpen(true);
  };

  const verifyOther = (deviceId: string) => {
    if (launcher && userId) void launcher.startVerification(userId, deviceId);
  };

  return (
    <Dialog
      labelledBy={titleId}
      onClose={onClose}
      width="var(--chat-dialog-width)"
      panelStyle={{ minHeight: 360 }}
    >
      <DialogHeader title={title} titleId={titleId} onClose={onClose} />
      <div
        className="flex flex-col gap-8 overflow-y-auto"
        style={{ padding: "var(--chat-dialog-body-padding)" }}
      >
        <section aria-label="This device" className="flex flex-col gap-4">
          <SectionLabel>This device</SectionLabel>
          <div className="flex items-start gap-3">
            {verified ? (
              <ShieldCheck
                aria-hidden
                size={20}
                strokeWidth={ICON_STROKE}
                className="mt-0.5 shrink-0 text-foreground/80"
              />
            ) : (
              <ShieldAlert
                aria-hidden
                size={20}
                strokeWidth={ICON_STROKE}
                className="mt-0.5 shrink-0 text-foreground/80"
              />
            )}
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-base font-light text-foreground/90">
                {current?.displayName || currentDeviceId || "This device"}
              </span>
              <span className="text-sm font-light text-muted-foreground">
                {devices.status === "loading" && !needsRecovery
                  ? "Checking…"
                  : verified
                    ? "Verified. Your messages are readable here."
                    : "Not verified. Older messages stay locked until you verify."}
              </span>
            </div>
          </div>
          {!verified && devices.status !== "loading" && (
            <VerificationPrimaryButton size="md" block onClick={verifyThisDevice}>
              Verify this device
            </VerificationPrimaryButton>
          )}
          <button
            type="button"
            disabled={!client}
            onClick={() => setRestoreOpen(true)}
            className="-mx-2 flex h-11 items-center self-start rounded-lg px-2 text-sm font-light text-muted-foreground outline-none transition-colors hover:text-foreground/80 focus-visible:shadow-neon-cyan disabled:opacity-40"
          >
            Use recovery key
          </button>
          <button
            type="button"
            onClick={() => setConfirmingSignOut(true)}
            className="-mx-2 flex h-11 items-center self-start rounded-lg px-2 text-sm font-light text-muted-foreground outline-none transition-colors hover:bg-foreground/[0.03] hover:text-foreground/80 focus-visible:shadow-neon-cyan"
          >
            Sign out
          </button>
        </section>
        <DeviceList state={devices} currentDeviceId={currentDeviceId} onVerify={verifyOther} />
        <BlockedPeople />
      </div>
      {confirmingSignOut && (
        <ConfirmDialog
          title="Sign out this device?"
          body="You will be signed out of Nurion Chat on this device. Your other sessions stay signed in."
          confirmLabel="Sign out"
          onConfirm={() => {
            setConfirmingSignOut(false);
            void signOut();
          }}
          onCancel={() => setConfirmingSignOut(false)}
        />
      )}
    </Dialog>
  );
}

function bestVerifiedPartner(
  devices: MatrixDevice[],
  currentDeviceId: string | null,
): MatrixDevice | null {
  const candidates = devices
    .filter(
      (device) =>
        device.deviceId !== currentDeviceId &&
        device.isCrossSignedByOwner &&
        device.isDehydrated !== true,
    )
    .sort((a, b) => (b.lastSeenTs ?? 0) - (a.lastSeenTs ?? 0));
  return candidates[0] ?? null;
}
